import { z } from 'zod'
import { instantSchema, wallClockSchema } from '../validators/temporal.validator.js'
import { calendarDaySchema, nullableCalendarDaySchema } from '../validators/calendar-day.validator.js'
import { eventLifecycleSchema } from '../enums/event-lifecycle.enum.js'
import { cancellationReasonSchema } from '../enums/cancellation-reason.enum.js'
import { lostReasonSchema } from '../enums/lost-reason.enum.js'
import { honeybookEventProblemSchema } from '../enums/honeybook-event-problem.enum.js'
import { eventSyncFieldSchema } from '../enums/event-sync-field.enum.js'
import { eventEditFieldSchema } from '../enums/event-edit-field.enum.js'
import { venueDtoSchema, venueSpaceDtoSchema } from './venue.dto.js'
import { floorObjectSchema } from './floor-object.dto.js'
import { furnitureItemDtoSchema } from './furniture-item.dto.js'
import { eventDocumentDtoSchema } from './event-document.dto.js'
import { phaseTypeSchema } from '../enums/phase-type.enum.js'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'
import { furnitureSourceSchema } from '../enums/furniture-source.enum.js'
import { invitedContactDtoSchema } from './user.dto.js'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'

/** A client contact on an event: the client's user id + identity + invite status. */
export const eventClientDtoSchema = invitedContactDtoSchema

export type EventClientDto = z.infer<typeof eventClientDtoSchema>

/**
 * The planner on an event: a vendor company, assigned for `planning`
 * (docs/planner-vendors.md).
 */
export const eventPlannerDtoSchema = z.object({
  assignmentId: z.string().uuid(),
  vendorId: z.string().uuid(),
  companyName: z.string(),
  /**
   * The company's person the venue and the couple deal with, and who is
   * written to — the same shape as a client contact, invitation state
   * included. Null when they have left, and the company's owners are written
   * to instead.
   */
  contact: invitedContactDtoSchema.nullable(),
})

export type EventPlannerDto = z.infer<typeof eventPlannerDtoSchema>

/**
 * A company on the event other than its planner, in one of the two ways a
 * company can be on one (docs/planner-vendors.md, decision 25).
 *
 * - `assigned` — a venue added it for a service, and can take it off. Its
 *   contact is written to, as the planner's is.
 * - `rental` — there because furniture of its own is rented on the event. Nobody
 *   adds or removes it: it comes with its first rental and goes with its last.
 *
 * A company can be both, and the planner can also be here through its rentals.
 */
export const eventVendorDtoSchema = z.discriminatedUnion('connection', [
  z.object({
    connection: z.literal('assigned'),
    assignmentId: z.string().uuid(),
    vendorId: z.string().uuid(),
    companyName: z.string(),
    /** Never `planning`: the planner is the event's `planner`, and the schema holds to that. */
    service: vendorServiceSchema.exclude(['planning']),
    contact: invitedContactDtoSchema.nullable(),
  }),
  z.object({
    connection: z.literal('rental'),
    vendorId: z.string().uuid(),
    companyName: z.string(),
  }),
])

export type EventVendorDto = z.infer<typeof eventVendorDtoSchema>

/**
 * How much of one item a phase can draw on: `owned` from the track's stock
 * available to this space (movable + pinned here), `rented` from event extras,
 * and their sum `available`. Placing more than `available` is a shortfall.
 */
/**
 * Whether a piece on a plan can be used there.
 *
 * - `available` — stock or a rental the plan can draw on.
 * - `removed` — taken off the event while this plan had it placed. The pieces
 *   stay on the plan, marked, until somebody takes them off; the plan cannot be
 *   saved until then.
 * - `unstocked` — a venue piece placed on the plan that the venue no longer
 *   stocks here, so every one of them is short.
 */
export const availabilityStatusSchema = z.enum(['available', 'removed', 'unstocked'])
export type AvailabilityStatus = z.infer<typeof availabilityStatusSchema>

export const phaseAvailabilityEntrySchema = z.object({
  furnitureItemId: z.string().uuid(),
  item: furnitureItemDtoSchema,
  /** Where this stock comes from — the planner groups the picker by it. */
  source: furnitureSourceSchema,
  owned: z.number().int().nonnegative(),
  rented: z.number().int().nonnegative(),
  available: z.number().int().nonnegative(),
  /** Defaulted, so a reply from an API that predates it still reads as usable. */
  status: availabilityStatusSchema.default('available'),
})

export type PhaseAvailabilityEntry = z.infer<typeof phaseAvailabilityEntrySchema>

/**
 * One timed segment of an event, in one space, with its own furniture map. The
 * phase inherits a space version; `frozen` means that binding is settled and the
 * phase is read-only — either explicitly locked (`lockedAt` set) or the event
 * date has passed. While not frozen it live-binds to the version effective on
 * the event's date. `space` is that space rendered at the phase's effective
 * version (its background + inventory), so the editor is self-contained.
 */
export const eventPhaseDtoSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  type: phaseTypeSchema,
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  venueSpaceId: z.string().uuid(),
  spaceVersionId: z.string().uuid(),
  sortOrder: z.number().int(),
  objects: z.array(floorObjectSchema),
  /**
   * What the setup crew is told about this space, as markdown.
   *
   * Written in the floor plan editor and rendered on the venue's public schedule
   * — the one field in the app whose contents reach an unauthenticated page.
   * Null until somebody writes it; an empty string would mean written and then
   * cleared, which is a different thing. See `docs/public-schedule.md`.
   *
   * `.default(null)` so a new web build reading an older API mid-deploy does not
   * fail to parse every phase it is sent. The same mistake was made with the
   * timeline's `hasFinished` and caught before it shipped: during a rolling
   * deploy the two halves are briefly different versions, and a field the
   * response does not carry yet must be absent-able rather than fatal.
   */
  instructions: z.string().nullable().default(null),
  frozen: z.boolean(),
  lockedAt: instantSchema.nullable(),
  /**
   * True when the venue's own people confirmed the plan: then only they may
   * reopen it, and the couple and planner are told to ask. Null while it is
   * unconfirmed, or when it was confirmed before this was kept. Defaulted so a
   * new web build reads an older API mid-deploy.
   */
  lockedByStaff: z.boolean().nullable().default(null),
  space: venueSpaceDtoSchema,
  availability: z.array(phaseAvailabilityEntrySchema),
})

export type EventPhaseDto = z.infer<typeof eventPhaseDtoSchema>

/** Summary shape for an event (list rows). */
export const eventDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /**
   * The day the event happens (`yyyy-MM-dd`), or null while it is an inquiry.
   *
   * A **day**, not a timestamp — the column is a Postgres `date`. It used to
   * travel as an ISO datetime whose time was always midnight, which is why a 14
   * May event rendered as "May 13" west of Greenwich and read as past from its
   * own small hours. An event that crosses midnight keeps the day it *starts*.
   */
  /**
   * What a person decided about this event — an inquiry, a booking, or a
   * booking let go.
   *
   * Distinct from a *status*: status adds `past`, which is a comparison against
   * now and so is computed where it is shown. See
   * `lib/src/enums/event-lifecycle.enum.ts`.
   */
  lifecycle: eventLifecycleSchema,
  date: nullableCalendarDaySchema,
  /**
   * The event's time window on `date` (both null while undated/untimed).
   *
   * Wall-clock times at the venue, never converted: the `HH:MM` lives directly
   * in the string and a time picker shows exactly what was stored. An end
   * earlier in the day than the start means the event runs past midnight.
   */
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  venueId: z.string().uuid(),
  venueName: z.string(),
  /** The event's cover photograph, signed for the browser; null if none yet. */
  coverImageUrl: browserAssetUrlSchema.nullable(),
  /**
   * The venue's photograph, for the events that have none of their own — which
   * is nearly all of them, since nothing in the app uploads a picture *of an
   * event*.
   *
   * An event happens somewhere, and that somewhere has a photograph already;
   * standing an event's page on a shipped stock manor instead was a picture of
   * a place the event is not. Carried flat rather than as the venue, because a
   * list row cannot afford a whole `VenueDto` — the detail DTO has one for other
   * reasons and ends up with both, which is the cost of the list being cheap.
   */
  venueCoverImageUrl: browserAssetUrlSchema.nullable(),
  /** Where the subject of that photograph is, so every crop keeps it. */
  venueCoverFocalPoint: focalPointSchema.nullable(),
  /**
   * The tracks this event occupies. Empty means venue-wide — every space at the
   * venue is available to it, which is what `spaceOnTrack` already did for a
   * null track.
   *
   * A set because an event may take several tracks when each is free for its
   * window. Writes stay single-valued until availability ships; see
   * `docs/event-scheduling.md`.
   *
   * Required again: it was optional only while an older API sent no `trackIds`
   * at all, and the retiring `eventTrackId` mirror covered its absence. Both
   * are gone.
   */
  trackIds: z.array(z.string().uuid()),
  /** The client contacts on this event (empty if none assigned yet). */
  clients: z.array(eventClientDtoSchema),
  /**
   * The company planning the event, if one is. Defaulted, not required, while
   * an API older than planners is still answering: its responses carry none.
   */
  planner: eventPlannerDtoSchema.nullable().default(null),
  /**
   * How many floor plans this event has, and how many are still open and
   * unconfirmed: a phase with no `lockedAt` still tracks the venue, so a remodel
   * or a restock can move the furniture someone arranged.
   *
   * `unconfirmedPhaseCount` is 0 once the event's date has passed. Those phases
   * are frozen by the date itself and can no longer be confirmed, so counting
   * them would be reporting a task nobody can do — and it saves every caller
   * re-deriving "is this event over?" from the clock, differently.
   */
  phaseCount: z.number().int().nonnegative(),
  unconfirmedPhaseCount: z.number().int().nonnegative(),
  guestCount: z.number().int().nonnegative(),
  /**
   * Venue staff's notes. Null for everyone else — clients, the planner and
   * vendors are always sent null, whatever is written — and when none are.
   * Defaulted while an older API, or the importer's parse, has none.
   */
  notes: z.string().nullable().default(null),
  /**
   * Whether the event is over, decided at the venue's clock.
   *
   * Derived and sent rather than worked out on the client, because working it
   * out from `date` is the mistake `phase-frozen.ts` documents at length: a
   * date compared against now is true from the first minute of the event's own
   * day, which for a Californian venue is five in the afternoon the day before.
   * The server has the venue's zone; nothing here does.
   *
   * Defaulted rather than required, because a deploy lands the web and the API
   * separately: for the minutes in between, a new client reads an old server's
   * response, and a *required* new field would fail the whole parse — taking
   * the event list, the event page and the calendar down with it rather than
   * just this flag. `false` degrades to "still editable", which is the safe way
   * to be briefly wrong. The inferred type stays `boolean`.
   */
  hasFinished: z.boolean().default(false),
  /**
   * The Social Tables event this was imported from, when it was imported. The
   * Prism catalog matches on it, so a renamed event is still recognized as
   * imported instead of being imported a second time.
   */
  prismEventUid: z.string().nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
})

export type EventDto = z.infer<typeof eventDtoSchema>

/** Full event: its venue (with space backgrounds), phases, and documents. What the editor loads. */
/**
 * One change to an event's lifecycle: what moved, when, who moved it, and — for
 * a cancellation — why.
 *
 * `from` is null for the first row of an event's life, which has nothing before
 * it. `byName` is null when there was genuinely nobody: a webhook, or the
 * migration that gave existing events their first row.
 *
 * The reason lives here rather than on the event because it belongs to the *act*
 * of canceling — an event canceled, restored and canceled again has two of
 * them, and a column could hold only the last. The same goes for why an inquiry
 * was lost (`lostReason`).
 */
export const eventStateChangeDtoSchema = z.object({
  from: eventLifecycleSchema.nullable(),
  to: eventLifecycleSchema,
  at: instantSchema,
  byName: z.string().nullable(),
  reasonCode: cancellationReasonSchema.nullable(),
  /** Set only on a move to `lost`. Defaulted while an older API is still answering. */
  lostReason: lostReasonSchema.nullable().default(null),
  note: z.string().nullable(),
})

export type EventStateChangeDto = z.infer<typeof eventStateChangeDtoSchema>

/** One thing HoneyBook changed on an event it manages, as shown. */
export const eventSyncChangeDtoSchema = z.object({
  at: instantSchema,
  /** See {@link eventSyncFieldSchema}. */
  field: eventSyncFieldSchema,
  before: z.string().nullable(),
  after: z.string().nullable(),
})

export type EventSyncChangeDto = z.infer<typeof eventSyncChangeDtoSchema>

/** A change somebody made to the event by hand, for its history: the guest count so far. */
export const eventEditDtoSchema = z.object({
  at: instantSchema,
  field: eventEditFieldSchema,
  /** As shown; null when there was nothing. */
  before: z.string().nullable(),
  /** As shown. For a vendor taken off by something done elsewhere, why it came off. */
  after: z.string().nullable(),
  /** Null once their account is gone. */
  byName: z.string().nullable(),
})

export type EventEditDto = z.infer<typeof eventEditDtoSchema>

/**
 * An event made from a HoneyBook project (docs/honeybook.plan.md, phase 4), for
 * the venue's staff: whether HoneyBook still manages it, whether its updates
 * are paused, and what keeps it from following the project.
 */
export const eventHoneybookDtoSchema = z.object({
  /** Its name, day, times, guests and couple follow HoneyBook and can't be changed here. */
  managed: z.boolean(),
  /** HoneyBook is disconnected, refusing the connection, or switched off: it stays as it was. */
  paused: z.boolean(),
  /** The project in HoneyBook's own app; null when its workspace isn't known. */
  openUrl: z.string().url().nullable(),
  /** HoneyBook had no times for it, so the venue's usual ones are in use. */
  usesDefaultTimes: z.boolean(),
  problem: honeybookEventProblemSchema.nullable(),
  /** The event already on the lane HoneyBook moved it onto, or that HoneyBook moved one of its own onto this one's. */
  clashWith: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** What HoneyBook changed, oldest first. */
  changes: z.array(eventSyncChangeDtoSchema),
  /** What the couple booked, in cents, as staff typed it; null until typed. Counts toward return on ad spend, and stays typable once HoneyBook no longer manages the event. */
  bookedValueCents: z.number().int().nonnegative().nullable().default(null),
})

export type EventHoneybookDto = z.infer<typeof eventHoneybookDtoSchema>

/** A date held for an inquiry until `endsOn`. It warns others and blocks nothing. */
export const eventHoldDtoSchema = z.object({
  id: z.string().uuid(),
  date: calendarDaySchema,
  endsOn: calendarDaySchema,
  createdAt: instantSchema,
})

export type EventHoldDto = z.infer<typeof eventHoldDtoSchema>

export const eventDetailDtoSchema = eventDtoSchema.extend({
  venue: venueDtoSchema,
  phases: z.array(eventPhaseDtoSchema),
  documents: z.array(eventDocumentDtoSchema),
  /**
   * Every company on the event but its planner: those assigned for a service,
   * then those here through their rented furniture. Defaulted while an older API
   * is still answering.
   */
  vendors: z.array(eventVendorDtoSchema).default([]),
  /**
   * The lifecycle history, oldest first.
   *
   * On the detail DTO rather than the list one: it is the answer to "what
   * happened to this booking", which is a question about one event. Optional
   * transitionally, so an older API's response still parses — see
   * `DEPLOY_NOTES.md`.
   */
  stateChanges: z.array(eventStateChangeDtoSchema).optional(),
  /**
   * The dates the couple asked about, earliest first, while the event is an
   * inquiry (or a lost one); `[]` once it is booked. Defaulted while an older
   * API is still answering.
   */
  requestedDates: z.array(calendarDaySchema).default([]),
  /**
   * The inquiry's active hold: not released, and ending today or later at the
   * venue. Null otherwise, and always null once it is booked. Defaulted while an
   * older API is still answering.
   */
  hold: eventHoldDtoSchema.nullable().default(null),
  /** For the venue's staff, on an event made from a HoneyBook project; null otherwise. */
  honeybook: eventHoneybookDtoSchema.nullable().default(null),
  /**
   * Changes people made by hand, oldest first, for the venue's staff; `[]` for
   * everyone else. Defaulted while an older API is still answering.
   */
  edits: z.array(eventEditDtoSchema).default([]),
  /**
   * The guest count follows HoneyBook, so nobody can change it here. On the
   * event for everyone, because a couple and their planner can otherwise set
   * it and need to know when they can't.
   */
  guestCountLocked: z.boolean().default(false),
})

export type EventDetailDto = z.infer<typeof eventDetailDtoSchema>

/** A company planning events the caller can see, for the events list's Planner filter. */
export const eventPlannerOptionDtoSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.string(),
})

export const eventPlannerOptionListDtoSchema = z.object({
  planners: z.array(eventPlannerOptionDtoSchema),
})

export type EventPlannerOptionListDto = z.infer<typeof eventPlannerOptionListDtoSchema>

export const eventListDtoSchema = z.object({
  events: z.array(eventDtoSchema),
  total: z.number().int().nonnegative(),
})

export type EventListDto = z.infer<typeof eventListDtoSchema>

/**
 * What deleting an event did to the client contacts on it. Reported so the
 * confirmation can be answered with the outcome rather than a guess: a client
 * who is also a contact on another event is kept, whichever choice was made.
 */
export const eventDeleteResultDtoSchema = z.object({
  clientsDeleted: z.number().int().nonnegative(),
  clientsDeactivated: z.number().int().nonnegative(),
  clientsKept: z.number().int().nonnegative(),
})

export type EventDeleteResultDto = z.infer<typeof eventDeleteResultDtoSchema>

/**
 * What an import's collaborator was put on the event as, and the event after.
 * `none` when they are a planning company's person and it was not made the
 * planner: `reason` says why, for the job log.
 */
export const eventCollaboratorResultDtoSchema = z.object({
  addedAs: z.enum(['client', 'planner', 'none']),
  /** The planning company, when they are its person; null for a client. */
  companyName: z.string().nullable(),
  reason: z.string().nullable(),
  detail: eventDetailDtoSchema,
})

export type EventCollaboratorResultDto = z.infer<typeof eventCollaboratorResultDtoSchema>
